/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import flow from 'lodash.flow';
import { when } from 'mobx';
import { getLogger } from 'src/core/services/telemetry/telemetryLogger';
import { HandlerGroupFactoryDeps, HandlerParams } from 'src/core/types/handler';
import { addDirtyState } from 'src/core/utils/decorators/addDirtyState';
import { getPatternService } from 'src/luis/core/services/patterns/patternService';
import { PatternSwagger } from 'src/luis/core/services/patterns/patternSwagger';
import { PatternStore } from 'src/luis/core/stores/patternStore';
import { loadIntentsHelper } from 'src/luis/modules/intents/intentHandlerGroup';
import { patternSwaggerToPatternStore } from 'src/luis/modules/patterns/patternMappers';
import { LuisId } from 'src/luis/types/Resource';

export type PatternHandlerGroup = ReturnType<typeof createPatternHandlerGroup>;

export const createPatternHandlerGroup = (dependencies: HandlerGroupFactoryDeps) => {
    const { rootStore } = dependencies;
    const logger = getLogger();

    /**
     * Loads the patterns for the given app and version
     * in the patterns store house.
     */
    const loadPatterns = async (params?: HandlerParams) => {
        const { context } = params;
        const { appId, versionId, forceRefresh } = context;
        const appVersionStore = rootStore.luisAppStoreHouse.findById(appId)?.appVersionStoreHouse.findById(versionId);
        const intentsStoreHouse = appVersionStore.intentStoreHouse;
        const { patternStoreHouse } = appVersionStore;

        if (intentsStoreHouse.isLoading) {
            await when(() => intentsStoreHouse.isLoaded === true);
        } else if (!intentsStoreHouse.isLoaded) {
            await loadIntentsHelper(rootStore)({ context });
        }

        if (!forceRefresh && (patternStoreHouse.isLoading || patternStoreHouse.isLoaded)) {
            return;
        }

        patternStoreHouse.setIsLoading(true);

        const logTimedActivity = logger.createTimedActivityLogger<PatternSwagger[]>('LoadPatterns');

        const patternSwaggers = await logTimedActivity(getPatternService({ context }).getPatterns());

        const patternStores = patternSwaggers.map(swagger =>
            patternSwaggerToPatternStore(swagger, intentsStoreHouse.findByName(swagger.intent).id)
        );

        patternStoreHouse.setItems(patternStores);
        patternStoreHouse.setIsLoading(false);
        patternStoreHouse.setIsLoaded(true);
    };

    /**
     * Creates a new pattern.
     *
     * @param param0.text The text of the pattern.
     * @param param0.intentId The pattern labeled intent id.
     */
    const createPattern = async ({ text, intentId, context }: HandlerParams<Pick<PatternStore, 'text' | 'intentId'>>) =>
        flow(async () => {
            const { appId, versionId } = context;
            const appVersionStore = rootStore.luisAppStoreHouse.findById(appId)?.appVersionStoreHouse.findById(versionId);
            const intentsStoreHouse = appVersionStore.intentStoreHouse;
            const intentName = intentsStoreHouse.findById(intentId).name;
            const logTimedActivity = logger.createTimedActivityLogger<PatternSwagger>('CreatePattern', () => ({ intentId, text }));

            const patternSwagger = await logTimedActivity(getPatternService({ context }).createPattern({ intentName, text }));

            appVersionStore.patternStoreHouse.add(
                patternSwaggerToPatternStore(patternSwagger, intentsStoreHouse.findByName(patternSwagger.intent).id)
            );
        }, addDirtyState({ appId: context.appId, versionId: context.versionId, rootStore }))();

    /**
     * Updates the patterns with the given ids.
     */
    const updatePatterns = async ({ patterns, context }: HandlerParams<{ patterns: Pick<PatternStore, 'text' | 'id' | 'intentId'>[] }>) =>
        flow(async () => {
            const { appId, versionId } = context;
            const appVersionStore = rootStore.luisAppStoreHouse.findById(appId)?.appVersionStoreHouse.findById(versionId);
            const intentsStoreHouse = appVersionStore.intentStoreHouse;
            const logTimedActivity = logger.createTimedActivityLogger('UpdatePatterns', () => ({
                ids: JSON.stringify(patterns.map(id => id))
            }));

            await logTimedActivity(
                getPatternService({ context }).updatePatterns(
                    patterns.map(pattern => ({ ...pattern, intentName: intentsStoreHouse.findById(pattern.intentId).name }))
                )
            );

            patterns.forEach(pattern => {
                const patternStore = appVersionStore.patternStoreHouse.findById(pattern.id);

                patternStore.setText(pattern.text);
                patternStore.setIntentId(pattern.intentId);
            });
        }, addDirtyState({ appId: context.appId, versionId: context.versionId, rootStore }))();

    /**
     * Deletes the patterns with the given ids.
     */
    const deletePatterns = async ({ patternsIds, context }: HandlerParams<{ patternsIds: LuisId[] }>) =>
        flow(async () => {
            const { appId, versionId } = context;
            const appVersionStore = rootStore.luisAppStoreHouse.findById(appId)?.appVersionStoreHouse.findById(versionId);
            const logTimedActivity = logger.createTimedActivityLogger('DeletePatterns', () => ({ ids: JSON.stringify(patternsIds) }));

            await logTimedActivity(getPatternService({ context }).deletePatterns(patternsIds));

            appVersionStore.patternStoreHouse.setItems(
                appVersionStore.patternStoreHouse.items.filter(pattern => patternsIds.indexOf(pattern.id) === -1)
            );
        }, addDirtyState({ appId: context.appId, versionId: context.versionId, rootStore }))();

    return {
        loadPatterns,
        createPattern,
        updatePatterns,
        deletePatterns
    };
};
